import { Box, Button, Stack, Typography } from "@mui/material";
import type {
  AssistantAction,
  AssistantContentRef,
  AssistantFile,
  AssistantMappingChoices,
} from "../../../../api/types/assistantTypes";
import type { ChatItem } from "../chatState";
import { getContentKey, isSameContent } from "../content/assistantContent";
import ContentCard from "../content/ContentCard";
import ActionCard from "./ActionCard";
import ChatFileChip from "./ChatFileChip";
import ChatImage from "./ChatImage";
import ChatMessageText from "./ChatMessageText";

interface Props {
  item: ChatItem;
  actionsDisabled: boolean;
  showReplies: boolean;
  openContent?: AssistantContentRef;
  onShowAsDialog: (imageUrl: string) => void;
  onActionDecision: (action: AssistantAction, approve: boolean, mappingChoices?: AssistantMappingChoices) => void;
  onOpenFile: (file: AssistantFile) => void;
  onReply: (reply: string) => void;
  onToggleContent: (content: AssistantContentRef) => void;
}

const ChatMessageContent = ({
  item,
  actionsDisabled,
  showReplies,
  openContent,
  onShowAsDialog,
  onActionDecision,
  onOpenFile,
  onReply,
  onToggleContent,
}: Props) => {
  return (
    <Box>
      <ChatMessageText item={item} />

      {item.contents && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {item.contents.map((content) => (
            <ContentCard
              key={getContentKey(content)}
              content={content}
              isOpen={isSameContent(content, openContent)}
              onToggleOpen={onToggleContent}
            />
          ))}
        </Stack>
      )}

      {item.isPending && item.statusText && item.lastStepState !== "Running" && (
        <Typography variant="caption" color="textSecondary">
          {item.statusText}
        </Typography>
      )}

      {item.images && item.images.length > 0 && (
        <Stack direction="row" sx={{ gap: 1, mt: 1, flexWrap: "wrap" }}>
          {item.images.map((image) => (
            <ChatImage key={image.url} image={image} onShow={onShowAsDialog} />
          ))}
        </Stack>
      )}

      {item.files && item.files.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: "wrap" }}>
          {item.files.map((file) => (
            <ChatFileChip
              key={file.id ?? `failed-${file.name}`}
              name={file.name}
              error={file.id ? undefined : "This file could not be saved."}
              onClick={file.id ? () => onOpenFile(file) : undefined}
            />
          ))}
        </Stack>
      )}

      {item.actions && item.actions.length > 0 && (
        <Stack sx={{ gap: 1, mt: 1 }}>
          {item.actions.map((action) => (
            <ActionCard
              key={action.callId}
              action={action}
              disabled={actionsDisabled}
              onDecision={onActionDecision}
              onShowImage={onShowAsDialog}
            />
          ))}
        </Stack>
      )}

      {showReplies && !item.isPending && item.replies && item.replies.length > 0 && (
        <Stack direction="row" sx={{ gap: 1, mt: 1, flexWrap: "wrap" }}>
          {item.replies.map((reply) => (
            <Button
              key={reply.label}
              variant="outlined"
              disabled={actionsDisabled}
              onClick={() => onReply(reply.messageText)}
            >
              {reply.label}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ChatMessageContent;
